/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ParticipantDetails — what one pass knows about its participant
 * --------------------------------------------------------------
 * Opened by the eye button on a History row. It shows the details the pass
 * kept when the person registered — name, username, email, phone — plus the
 * pass's own code, status and times.
 *
 * No requests: every field is already on the row the list loaded.
 *
 * A pass issued before these details were saved has none, and says so. A
 * field the account never had is shown as "—", so the organizer can see that
 * it is missing rather than wonder whether it failed to load.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { clockIn } from './checkInRules';

const STATUS_LABEL = { CHECKED_OUT: 'Checked out', CHECKED_IN: 'Checked in', ISSUED: 'Not arrived' };

const FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['user_name', 'Username'],
  ['email', 'Email'],
  ['mobile_number', 'Mobile'],
  // Given only when the booker named somebody else for the seat.
  ['gender', 'Gender'],
  ['age', 'Age'],
];

const GENDER_LABEL = { FEMALE: 'Female', MALE: 'Male', PREFER_NOT_TO_SAY: 'Prefer not to say' };

function value(info, key) {
  const raw = String(info?.[key] ?? '').trim();
  if (!raw) return null;
  if (key === 'gender') return GENDER_LABEL[raw.toUpperCase()] || raw;
  if (key !== 'mobile_number') return raw;
  const code = String(info?.country_code ?? '').trim();
  return code ? `${code} ${raw}` : raw;
}

function ParticipantDetails({ row, timezone, onClose }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const info = row.info;
  const status = STATUS_LABEL[row.status] || STATUS_LABEL.ISSUED;

  return createPortal(
    <div className="ochd-scrim" role="presentation" onClick={onClose}>
      <div
        className="ochd"
        role="dialog"
        aria-modal="true"
        aria-label={`Details for pass ${row.passcode}`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="ochd-head">
          <div>
            <h2>{row.name || 'No name on this pass'}</h2>
            <p><span className="ochk-code">{row.passcode || '—'}</span></p>
          </div>
          <button type="button" className="ochk-iconbtn" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        <p className="org-lab ochd-lab">From their registration</p>
        {info ? (
          <dl className="ochd-list">
            {FIELDS.map(([key, label]) => (
              <div key={key} className="ochd-row">
                <dt>{label}</dt>
                <dd className={value(info, key) ? '' : 'is-empty'}>{value(info, key) || '—'}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="ochd-none">
            Nothing was saved with this pass. It was issued before participant details were kept,
            so only the passcode identifies it.
          </p>
        )}

        <p className="org-lab ochd-lab">This pass</p>
        <dl className="ochd-list">
          <div className="ochd-row">
            <dt>Status</dt>
            <dd><span className={`ochh-status is-${row.status === 'CHECKED_OUT' ? 'out' : (row.status === 'CHECKED_IN' ? 'in' : 'none')}`}>{status}</span></dd>
          </div>
          <div className="ochd-row">
            <dt>Checked in</dt>
            <dd className={row.inAt ? '' : 'is-empty'}>{row.inAt ? clockIn(row.inAt, timezone) : '—'}</dd>
          </div>
          <div className="ochd-row">
            <dt>Checked out</dt>
            <dd className={row.outAt && row.status === 'CHECKED_OUT' ? '' : 'is-empty'}>
              {row.outAt && row.status === 'CHECKED_OUT' ? clockIn(row.outAt, timezone) : '—'}
            </dd>
          </div>
        </dl>
      </div>
    </div>,
    document.querySelector('.org-shell') || document.body
  );
}

export default ParticipantDetails;
